import { Clip, MontageVersion, Track, Transform, TextStyle } from '../types';

export const ORIGINAL_MONTAGE_NAME = 'Version originale';
export const ORIGINAL_MONTAGE_PROMPT = 'Clips originaux';
export const AUTO_MONTAGE_PROMPT = 'Version creee automatiquement avant edition';

interface SerializableSyntheData {
    backgroundColor?: string;
    tracks: Pick<Track, 'id' | 'label' | 'accepts' | 'isMagnetic' | 'isVisible' | 'isMuted' | 'volume'>[];
    clips: SerializableClip[];
}

interface SerializableClip {
    id: string;
    name: string;
    type: Clip['type'];
    url?: string;
    trackId: string;
    start: number;
    duration: number;
    originalDuration: number;
    trimStart: number;
    transform: Transform | null;
    text?: string;
    style?: TextStyle;
    opacity: number;
    fadeInDuration: number;
    fadeOutDuration: number;
    endTransform?: Transform;
    volume?: number;
    playbackRate?: number;
    clipRole?: Clip['clipRole'];
    activeVideoVersion?: string;
    activeAudioSource?: string;
    activeImageMask?: string;
    syntheData?: SerializableSyntheData;
}

const cloneWithJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const serializeTracks = (tracks: Track[]) =>
    tracks.map(track => ({
        id: track.id,
        label: track.label,
        accepts: [...track.accepts],
        isMagnetic: track.isMagnetic,
        isVisible: track.isVisible,
        isMuted: track.isMuted,
        volume: track.volume,
    }));

const serializeClip = (clip: Clip): SerializableClip => ({
    id: clip.id,
    name: clip.name,
    type: clip.type,
    url: clip.url,
    trackId: clip.trackId,
    start: clip.start,
    duration: clip.duration,
    originalDuration: clip.originalDuration,
    trimStart: clip.trimStart,
    transform: clip.transform ? { ...clip.transform } : null,
    text: clip.text,
    style: clip.style ? { ...clip.style } : undefined,
    opacity: clip.opacity,
    fadeInDuration: clip.fadeInDuration,
    fadeOutDuration: clip.fadeOutDuration,
    endTransform: clip.endTransform ? { ...clip.endTransform } : undefined,
    volume: clip.volume,
    playbackRate: clip.playbackRate,
    clipRole: clip.clipRole,
    activeVideoVersion: clip.activeVideoVersion,
    activeAudioSource: clip.activeAudioSource,
    activeImageMask: clip.activeImageMask,
    syntheData: clip.syntheData
        ? {
            backgroundColor: clip.syntheData.backgroundColor,
            tracks: serializeTracks(clip.syntheData.tracks),
            clips: clip.syntheData.clips.map(serializeClip),
        }
        : undefined,
});

export const cloneClipsForMontage = (clips: Clip[]): Clip[] => cloneWithJson(clips);

export const getMontageFingerprint = (clips: Clip[]): string =>
    JSON.stringify(clips.map(serializeClip));

export const isOriginalMontageVersion = (version: MontageVersion | null | undefined): boolean =>
    !!version && version.name === ORIGINAL_MONTAGE_NAME;

export interface AutoMontageForkPlan {
    nextVersions: MontageVersion[];
    nextVersionId: string;
}

export const buildAutoMontageForkPlan = (
    existingVersions: MontageVersion[],
    sourceClips: Clip[]
): AutoMontageForkPlan => {
    const nextVersions = cloneWithJson(existingVersions);

    if (!nextVersions.some(version => version.name === ORIGINAL_MONTAGE_NAME)) {
        nextVersions.push({
            id: `version_originale_${Date.now()}`,
            name: ORIGINAL_MONTAGE_NAME,
            clips: cloneClipsForMontage(sourceClips),
            prompt: ORIGINAL_MONTAGE_PROMPT,
        });
    }

    const montageCount = nextVersions.filter(version => version.name.startsWith('Montage')).length;
    const nextVersionId = `version_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    nextVersions.push({
        id: nextVersionId,
        name: `Montage ${montageCount + 1}`,
        clips: cloneClipsForMontage(sourceClips),
        prompt: AUTO_MONTAGE_PROMPT,
    });

    return {
        nextVersions,
        nextVersionId,
    };
};
